import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { AddRuleProps, useAddRuleModal } from "./AddRuleModalHook";
import {
  CAPTION_ADD_RULE_MODAL_PENDING_AGENT_EDIT,
  CAPTION_BASE_TABLE_TOTALS,
  CAPTION_TT_ROW_COLUMN_WARNING,
} from "../validationCaptions";
import { MessageBar, MessageBarType, Modal, TeachingBubble } from "@fluentui/react";
import { ChoiceGroup, IChoiceGroupOption } from "@fluentui/react/lib/ChoiceGroup";
import { contentStyles } from "../../../layout/ActionModal";
import HeaderPanel from "../../../layout/HeaderPanel";
import { formatMessage, multiline } from "../../../../utils/helpers/LanguageHelper";
import FooterPanel from "../../../layout/FooterPanel";
import { IconButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { TextField } from "@fluentui/react/lib/TextField";
import { RuleInputEditor } from "../../../generic/RuleInputEditor";
import { ConsistencyRuleEditor } from "./ConsistencyRuleEditor";
import { RuleType } from "../../../interfaces/Rule";
import { useAppSelector } from "../../../../redux/hooks/hooks";
import { selectValidationCoObjects } from "../../../../redux/feature/validation/validation-slice";
import { CoObjectItem } from "../../../interfaces/CoObject";
import { CoObjectTypeEnum } from "../../../../api/models";
import { getAllCoObjects } from "../../../../utils/helpers/ContentControlHelper";
import { TableTotalsRuleEditor } from "./TableTotalsRuleEditor";

const WarningBar: React.FC = () => (
  <div
    style={{
      backgroundColor: "var(--color-orange, #ff8c00)",
      color: "white",
      borderRadius: 4,
      padding: "6px 8px",
      fontSize: 12,
      marginTop: 8,
    }}
  >
    {formatMessage(CAPTION_TT_ROW_COLUMN_WARNING)}
  </div>
);

export const AddRuleModal: FunctionComponent<AddRuleProps> = (props) => {
  const {
    ruleTitle,
    setRuleTitle,
    ruleScript,
    setRuleScript,
    ruleType,
    setRuleType,
    selectedTagIds,
    setSelectedTagIds,
    tableTotalsTableId,
    setBoundTable,
    addends,
    addAddends,
    removeAddend,
    replaceAddend,
    subtotal,
    setSubtotal,
    clearSubtotal,
    handleBack,
    handleSave,
    isSaveEnabled,
    ruleTitleError,
    ruleScriptError,
    showValidationInfo,
    setShowValidationInfo,
    validationInfoButtonRef,
    hasPendingAgentEdit,
  } = useAddRuleModal(props);

  const reduxCoObjects = useAppSelector(selectValidationCoObjects);
  // Freshly fetched objects captured by the refresh-on-open effect below; null
  // until the first getAllCoObjects() call resolves after the modal opens.
  const [freshCoObjects, setFreshCoObjects] = useState<CoObjectItem[] | null>(null);

  // Re-fetch document objects every time the modal is opened. reduxCoObjects is
  // loaded ONCE at ValidationPanel mount (ValidationPanelHook.ts) and is never
  // invalidated when a new TABLE object is created in the document, so a table
  // added after boot would stay missing from the picker below until a full
  // side-panel reload (IDS-7253). AddRuleModal is remounted on every open
  // ({showAddRulesModal && <AddRuleModal isShown={showAddRulesModal} .../>} in
  // RulesPanel.tsx), so this effect re-runs on every open even though isShown
  // itself never toggles while the component stays mounted. Mirrors the
  // refresh-on-open fix already applied to AddTagModalHook (IDS-6546): prefer
  // the fresh result once it arrives, falling back to the Redux snapshot until
  // then so there is no empty-list flash on first render.
  useEffect(() => {
    if (!props.isShown) return;
    getAllCoObjects(["TABLE"])
      .then((all) => {
        const fresh = all.filter((o) => o.source?.elabResultType !== "IMAGE_STRINGBASE64");
        setFreshCoObjects(fresh);
      })
      .catch((e) => console.error("[AddRuleModal] Failed to refresh table objects on open:", e));
  }, [props.isShown]);

  const coObjects = freshCoObjects ?? reduxCoObjects;
  const tables = useMemo(
    () =>
      coObjects
        .filter(
          (o): o is CoObjectItem & { id: string; contentControlId: number } =>
            o.info.objectType === CoObjectTypeEnum.TABLE && o.id !== undefined && o.contentControlId !== undefined
        )
        .map((o) => ({
          item: o,
          id: o.id,
          contentControlId: o.contentControlId,
          name: o.info.objectName || String(o.contentControlId),
        })),
    [coObjects]
  );

  const ruleTypeOptions: IChoiceGroupOption[] = [
    {
      key: String(RuleType.CUSTOM),
      text: formatMessage("cof.base.custom"),
      styles: { field: { fontSize: "12px" }, choiceFieldWrapper: { fontSize: "12px" } },
    },
    {
      key: String(RuleType.CONSISTENCY),
      text: formatMessage("cof.base.consistency"),
      styles: { field: { fontSize: "12px" }, choiceFieldWrapper: { fontSize: "12px" } },
    },
    {
      key: String(RuleType.TABLE_TOTALS),
      text: formatMessage(CAPTION_BASE_TABLE_TOTALS),
      styles: { field: { fontSize: "12px" }, choiceFieldWrapper: { fontSize: "12px" } },
    },
  ];

  const isConsistency = ruleType === RuleType.CONSISTENCY;
  const isTableTotals = ruleType === RuleType.TABLE_TOTALS;
  const showBottomSection = !isTableTotals || tableTotalsTableId !== undefined;

  const renderRuleEditor = () => {
    if (isConsistency) {
      return (
        <ConsistencyRuleEditor
          tags={props.tags.filter((t) => !t.isDeleted)}
          selectedTagIds={selectedTagIds}
          onChange={setSelectedTagIds}
        />
      );
    }
    if (isTableTotals) {
      return (
        <TableTotalsRuleEditor
          tables={tables}
          tableId={tableTotalsTableId}
          addends={addends}
          subtotal={subtotal}
          onBindTable={(t) => setBoundTable(t.id)}
          onAddAddends={addAddends}
          onRemoveAddend={removeAddend}
          onSetSubtotal={setSubtotal}
          onClearSubtotal={clearSubtotal}
          onReplaceAddend={replaceAddend}
        />
      );
    }
    return (
      <>
        <div
          style={{
            color: "var(--color-grey)",
            fontSize: "12px",
            marginBottom: "8px",
          }}
        >
          {formatMessage("cof.validation.info.ruleinputeditor")}
        </div>
        <div
          style={{
            fontWeight: "bold",
            color: "var(--color-blue)",
            fontSize: "14px",
          }}
        >
          {formatMessage("cof.validation.title.ruleinputeditor")}{" "}
          <span style={{ color: "var(--color-error, #a4262c)" }}>*</span>
          <span ref={validationInfoButtonRef} style={{ display: "inline-block" }}>
            <IconButton
              id={"infoButtonId"}
              aria-label="Info"
              iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
              onClick={() => setShowValidationInfo(true)}
            />
          </span>
          {showValidationInfo && (
            <TeachingBubble
              calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
              styles={{ bodyContent: { padding: "12px 12px" } }}
              target={validationInfoButtonRef.current}
              hasCloseButton
              onDismiss={() => setShowValidationInfo(false)}
              headline={formatMessage("cof.validation.title.ruleinputeditor")}
            >
              {multiline(formatMessage("cof.validation.info.validysyntax"))}
            </TeachingBubble>
          )}
        </div>
        <RuleInputEditor value={ruleScript} onChange={setRuleScript} tags={props.tags} />
        {ruleScriptError && (
          <div
            style={{
              color: "var(--color-error, #a4262c)",
              fontSize: "12px",
              marginTop: "5px",
              fontWeight: "400",
            }}
          >
            {ruleScriptError}
          </div>
        )}
      </>
    );
  };

  return (
    <Modal
      titleAriaId={"titleId"}
      isOpen={props.isShown}
      containerClassName={contentStyles.container}
      styles={{
        scrollableContent: {
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          paddingBottom: "70px",
          boxSizing: "border-box",
        },
      }}
    >
      <HeaderPanel applyPadding>
        {formatMessage(props.selectedRule ? "cof.validation.title.ruleediting" : "cof.validation.title.rulecreation")}
      </HeaderPanel>
      {hasPendingAgentEdit && (
        <MessageBar messageBarType={MessageBarType.warning}>
          {formatMessage(CAPTION_ADD_RULE_MODAL_PENDING_AGENT_EDIT)}
        </MessageBar>
      )}
      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          padding: "0 0.5rem",
        }}
      >
        {/* Type selector – hidden in edit mode to avoid changing type of existing rule */}
        {!props.selectedRule && (
          <div style={{ marginBottom: "12px" }}>
            <ChoiceGroup
              selectedKey={String(ruleType)}
              options={ruleTypeOptions}
              onChange={(_e, option) => option && setRuleType(option.key as RuleType)}
              styles={{
                flexContainer: {
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                },
              }}
            />
          </div>
        )}

        {renderRuleEditor()}
      </div>
      {showBottomSection && (
        <>
          <hr style={{ margin: "5px 0 0 0" }} />
          <div style={{ padding: "0 0.5rem" }}>
            <div
              style={{
                fontWeight: "bold",
                color: "var(--color-blue)",
                fontSize: "14px",
              }}
            >
              {formatMessage("cof.validation.title.rulename")}{" "}
              <span style={{ color: "var(--color-error, #a4262c)" }}>*</span>
            </div>
            <TextField
              value={ruleTitle}
              onChange={(_e, v) => setRuleTitle(v ?? "")}
              errorMessage={ruleTitleError}
              aria-label={formatMessage("cof.validation.title.rulename")}
            />
            {isTableTotals && <WarningBar />}
          </div>
        </>
      )}
      <FooterPanel>
        <PrimaryButton id="back_button" data-testid="back" onClick={handleBack} text={formatMessage("cof.base.back")} />
        {showBottomSection && (
          <PrimaryButton
            id="confirm_button"
            data-testid="save"
            onClick={handleSave}
            text={formatMessage(props.selectedRule ? "cof.base.update" : "cof.base.save")}
            disabled={!isSaveEnabled}
            style={{ float: "right" }}
          />
        )}
      </FooterPanel>
    </Modal>
  );
};
